import React, { useState, useEffect, useMemo } from 'react'
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { analyticsApi } from '../../services/api'
import type { DateRangeOption } from '../../hooks/useDashboard'
import DateRangeFilter from '../../components/dashboard/DateRangeFilter'

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunTotals {
  input_tokens: number
  output_tokens: number
  cached_tokens: number
}

interface WorkflowRun {
  run_key: string
  conversation_id: string | null
  status: string | null
  start_time: string | null
  end_time: string | null
  duration_seconds: number
  totals: RunTotals
  estimated_cost_usd: number
}

interface WorkflowRunsResponse {
  total_runs: number
  runs: WorkflowRun[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function fmtCost(n: number): string {
  if (n === 0) return '$0.00'
  if (n < 0.01) return `$${n.toFixed(6)}`
  return `$${n.toFixed(4)}`
}

function fmtDuration(s: number): string {
  if (!s || s <= 0) return '—'
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const sec = Math.round(s % 60)
  return `${m}m ${sec}s`
}

function toDateLabel(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function dateRangeToIso(
  range: DateRangeOption,
  customStart?: Date,
  customEnd?: Date,
): { from: string; to: string } {
  const now = new Date()
  const toIso = (d: Date) => d.toISOString()
  if (range === 'custom' && customStart && customEnd) {
    return { from: toIso(customStart), to: toIso(customEnd) }
  }
  const to = toIso(now)
  if (range === 'last_7_days') {
    const from = new Date(now); from.setDate(from.getDate() - 7); from.setHours(0, 0, 0, 0)
    return { from: toIso(from), to }
  }
  if (range === 'last_30_days') {
    const from = new Date(now); from.setDate(from.getDate() - 30); from.setHours(0, 0, 0, 0)
    return { from: toIso(from), to }
  }
  if (range === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    return { from: toIso(from), to }
  }
  const from = new Date(now); from.setHours(0, 0, 0, 0)
  return { from: toIso(from), to }
}

// ─── Session cache (cleared on page refresh) ──────────────────────────────────

const _cache = new Map<string, WorkflowRunsResponse>()

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  workflowName: string
  onBack: () => void
  initialDateRange?: DateRangeOption
  initialCustomStart?: Date
  initialCustomEnd?: Date
}

const PAGE_SIZE = 20

const WorkflowDetailView: React.FC<Props> = ({ workflowName, onBack, initialDateRange = 'last_7_days', initialCustomStart, initialCustomEnd }) => {
  const [dateRange, setDateRange] = useState<DateRangeOption>(initialDateRange)
  const [customStart, setCustomStart] = useState<Date | undefined>(initialCustomStart)
  const [customEnd, setCustomEnd] = useState<Date | undefined>(initialCustomEnd)
  const [data, setData] = useState<WorkflowRunsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  function handleDateChange(range: DateRangeOption, start?: Date, end?: Date) {
    setDateRange(range)
    setCustomStart(start)
    setCustomEnd(end)
    setPage(0)
  }

  function handleExport() {
    if (!data) return
    const wb = XLSX.utils.book_new()

    // Sheet 1: Summary
    const totalTokens = data.runs.reduce((s, r) => s + (r.totals?.input_tokens || 0) + (r.totals?.output_tokens || 0) + (r.totals?.cached_tokens || 0), 0)
    const totalCost = data.runs.reduce((s, r) => s + (r.estimated_cost_usd || 0), 0)
    const summaryRows = [
      ['Metric', 'Value'],
      ['Total Runs', data.total_runs],
      ['Completed', completedCount],
      ['Failed', failedCount],
      ['Total Tokens', totalTokens],
      ['Estimated Cost (USD)', totalCost],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary')

    // Sheet 2: Chart Data (Runs + Tokens by day)
    const chartHeader = ['Date', 'Runs', 'Total Tokens']
    const chartRows = chartData.map(d => [d.date, d.runs, d.total_tokens])
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([chartHeader, ...chartRows]), 'Chart Data')

    // Sheet 3: All Conversation Runs
    const runsHeader = ['Operation ID', 'Status', 'Start Time', 'End Time', 'Duration (s)', 'Input Tokens', 'Output Tokens', 'Cached Tokens', 'Est. Cost (USD)']
    const runsRows = data.runs.map(r => [
      r.conversation_id || r.run_key,
      r.status === 'failed' ? 'Failed' : 'Completed',
      r.start_time ? new Date(r.start_time).toLocaleString() : '',
      r.end_time ? new Date(r.end_time).toLocaleString() : '',
      r.duration_seconds || 0,
      r.totals?.input_tokens || 0,
      r.totals?.output_tokens || 0,
      r.totals?.cached_tokens || 0,
      r.estimated_cost_usd || 0,
    ])
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([runsHeader, ...runsRows]), 'Conversation Runs')

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${workflowName}-${dateRange}.xlsx`)
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { from, to } = dateRangeToIso(dateRange, customStart, customEnd)
      const cacheKey = `${workflowName}:${from}:${to}`
      const cached = _cache.get(cacheKey)
      if (cached) {
        setData(cached)
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const resp = await analyticsApi.getWorkflowRuns(workflowName, { from, to })
        if (!cancelled) {
          _cache.set(cacheKey, resp.data)
          setData(resp.data)
        }
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load workflow runs')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [workflowName, dateRange, customStart, customEnd])

  const completedCount = data ? data.runs.filter(r => r.status !== 'failed').length : 0
  const failedCount = data ? data.runs.filter(r => r.status === 'failed').length : 0

  const chartData = useMemo(() => {
    if (!data?.runs.length) return []
    const byDay: Record<string, { date: string; runs: number; total_tokens: number }> = {}
    for (const run of data.runs) {
      const label = toDateLabel(run.start_time)
      if (!label) continue
      if (!byDay[label]) byDay[label] = { date: label, runs: 0, total_tokens: 0 }
      byDay[label].runs += 1
      byDay[label].total_tokens +=
        (run.totals?.input_tokens || 0) +
        (run.totals?.output_tokens || 0) +
        (run.totals?.cached_tokens || 0)
    }
    return Object.values(byDay).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [data])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 font-mono">{workflowName}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Workflow run history &amp; token usage</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            selectedRange={dateRange}
            customStartDate={customStart}
            customEndDate={customEnd}
            onChange={handleDateChange}
          />
          <button
            onClick={handleExport}
            disabled={!data || loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
            </svg>
            Export to CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Summary pills */}
      {!loading && data && (
        <div className="flex gap-4 flex-wrap">
          <div className="bg-white rounded-lg border border-gray-200 px-5 py-3 shadow-sm">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Runs</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{fmt(data.total_runs)}</p>
          </div>
          <div className="bg-white rounded-lg border border-green-200 px-5 py-3 shadow-sm">
            <p className="text-xs text-green-600 uppercase tracking-wide font-medium">Completed</p>
            <p className="text-2xl font-bold text-green-700 mt-0.5">{fmt(completedCount)}</p>
          </div>
          <div className="bg-white rounded-lg border border-red-200 px-5 py-3 shadow-sm">
            <p className="text-xs text-red-500 uppercase tracking-wide font-medium">Failed</p>
            <p className="text-2xl font-bold text-red-600 mt-0.5">{fmt(failedCount)}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-5 py-3 shadow-sm">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Tokens</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">
              {fmt(data.runs.reduce((s, r) => s + (r.totals?.input_tokens || 0) + (r.totals?.output_tokens || 0) + (r.totals?.cached_tokens || 0), 0))}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-5 py-3 shadow-sm">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Estimated Cost</p>
            <p className="text-2xl font-bold text-violet-700 mt-0.5">
              {fmtCost(data.runs.reduce((s, r) => s + (r.estimated_cost_usd || 0), 0))}
            </p>
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Workflow Runs</h3>
          {loading ? (
            <div className="h-48 animate-pulse bg-gray-100 rounded" />
          ) : chartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={192}>
              <BarChart data={chartData} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => [fmt(v as number), 'Runs']}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Bar dataKey="runs" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Total Tokens</h3>
          {loading ? (
            <div className="h-48 animate-pulse bg-gray-100 rounded" />
          ) : chartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={192}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="wfTokenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v)} />
                <Tooltip
                  formatter={(v) => [fmt(v as number), 'Tokens']}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Area dataKey="total_tokens" stroke="#8b5cf6" strokeWidth={2} fill="url(#wfTokenGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Runs table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Conversation Runs</h3>
        </div>
        {loading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded" />)}
          </div>
        ) : !data?.runs.length ? (
          <div className="p-8 text-center text-sm text-gray-400">No runs found for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Operation ID</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Start Time</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">End Time</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Input Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Output Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Cached Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Est. Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.runs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((run) => (
                  <tr key={run.run_key} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 font-mono text-xs text-gray-600 max-w-[200px] truncate" title={run.conversation_id || run.run_key}>
                      {run.conversation_id || run.run_key}
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${run.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {run.status === 'failed' ? 'Failed' : 'Completed'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {run.start_time ? new Date(run.start_time).toLocaleString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {run.end_time ? new Date(run.end_time).toLocaleString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmtDuration(run.duration_seconds)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(run.totals?.input_tokens || 0)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(run.totals?.output_tokens || 0)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(run.totals?.cached_tokens || 0)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-violet-700">{fmtCost(run.estimated_cost_usd || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {data?.runs && data.runs.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.runs.length)} of {data.runs.length}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= data.runs.length}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkflowDetailView
